import React, { useState, useEffect } from 'react';
import { useVCOnboarding } from '../../../contexts/VCOnboardingContext';
import {
  VCInsights,
  BusinessModel,
  KeyMetric,
  BUSINESS_MODEL_LABELS,
  KEY_METRIC_LABELS,
  KEY_METRIC_DESCRIPTIONS,
} from '../../../types/vcOnboarding';
import styles from '../VCOnboarding.module.css';

const Step4Insights: React.FC = () => {
  const { data, saveStep, nextStep, previousStep, loading } = useVCOnboarding();
  
  const [formData, setFormData] = useState<VCInsights>({
    businessModels: [],
    keyMetrics: [],
    customMetrics: '',
  });
  
  const [errors, setErrors] = useState<Partial<Record<keyof VCInsights, string>>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (data.step4_insights) {
      setFormData(data.step4_insights);
    }
  }, [data.step4_insights]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof VCInsights, string>> = {};
    
    if (formData.businessModels.length === 0) {
      newErrors.businessModels = 'Please select at least one business model';
    }
    
    if (formData.keyMetrics.length === 0) {
      newErrors.keyMetrics = 'Please select at least one key metric';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      await saveStep(4, formData);
      nextStep();
    } catch (error) {
      console.error('Error saving step 4:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleBusinessModel = (model: BusinessModel) => {
    setFormData(prev => ({
      ...prev,
      businessModels: prev.businessModels.includes(model)
        ? prev.businessModels.filter(m => m !== model)
        : [...prev.businessModels, model],
    }));
    if (errors.businessModels) {
      setErrors(prev => ({ ...prev, businessModels: undefined }));
    }
  };

  const toggleMetric = (metric: KeyMetric) => {
    setFormData(prev => ({
      ...prev,
      keyMetrics: prev.keyMetrics.includes(metric)
        ? prev.keyMetrics.filter(m => m !== metric)
        : [...prev.keyMetrics, metric],
    }));
    if (errors.keyMetrics) {
      setErrors(prev => ({ ...prev, keyMetrics: undefined }));
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.card}>
        <h2 className={styles.cardTitle}>What insights matter most?</h2>
        <p className={styles.cardDescription}>
          We&apos;ll prioritize these metrics in your portfolio updates
        </p>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Portfolio Business Models (Select all that apply)
          </label>
          <div className={styles.toggleGroup}>
            {(Object.keys(BUSINESS_MODEL_LABELS) as BusinessModel[]).map((model) => (
              <button
                key={model}
                type="button"
                className={`${styles.toggleButton} ${
                  formData.businessModels.includes(model) ? styles.active : ''
                }`}
                onClick={() => toggleBusinessModel(model)}
                disabled={isSaving || loading}
              >
                {BUSINESS_MODEL_LABELS[model]}
              </button>
            ))}
          </div>
          {errors.businessModels && (
            <div className={styles.errorMessage}>{errors.businessModels}</div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>
            Key Metrics (Select all that apply)
          </label>
          <div className={styles.integrationGrid} style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
            {(Object.keys(KEY_METRIC_LABELS) as KeyMetric[]).map((metric) => (
              <button
                key={metric}
                type="button"
                className={`${styles.metricCard} ${
                  formData.keyMetrics.includes(metric) ? styles.selected : ''
                }`}
                onClick={() => toggleMetric(metric)}
                disabled={isSaving || loading}
              >
                <div className={styles.metricContent}>
                  <div className={styles.metricTitle}>{KEY_METRIC_LABELS[metric]}</div>
                  <div className={styles.metricDescription}>
                    {KEY_METRIC_DESCRIPTIONS[metric]}
                  </div>
                </div>
                <div className={styles.metricCheckbox}>
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </button>
            ))}
          </div>
          {errors.keyMetrics && (
            <div className={styles.errorMessage}>{errors.keyMetrics}</div>
          )}
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="customMetrics">
            Custom Metrics (Optional)
          </label>
          <textarea
            id="customMetrics"
            className={styles.textarea}
            placeholder="e.g., Carbon offset tons, API calls processed, Patents filed…"
            value={formData.customMetrics || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              customMetrics: e.target.value,
            }))}
            disabled={isSaving || loading}
          />
          <p style={{ fontSize: '0.8125rem', color: '#6b7280', marginTop: '0.5rem' }}>
            For non-traditional business models or industry-specific KPIs
          </p>
        </div>

        <div className={styles.buttonGroup}>
          <button
            type="button"
            className={`${styles.button} ${styles.buttonSecondary}`}
            onClick={previousStep}
            disabled={isSaving || loading}
          >
            <svg className={styles.buttonIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>
          <button
            type="submit"
            className={`${styles.button} ${styles.buttonPrimary}`}
            disabled={isSaving || loading}
          >
            {isSaving ? (
              <>
                <span className={styles.loadingSpinner} />
                Saving...
              </>
            ) : (
              <>
                Continue
                <svg className={styles.buttonIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default Step4Insights;

